import { Fragment, useEffect, useMemo, useState } from "react";
import {
  api,
  type PredictionHistoryGame,
  type PredictionHistoryResponse,
  type SlateGame,
  type SlateResponse,
  type SlateWindow,
} from "../lib/api";
import { useAuth } from "../lib/auth";
import { useDisplayPreferences } from "../lib/displayPreferences";

function fmtProb(p: number | null | undefined): string {
  if (p == null || Number.isNaN(p)) return "—";
  return `${(p * 100).toFixed(1)}%`;
}

function fmtDelta(d: number | null | undefined): string {
  if (d == null || Number.isNaN(d)) return "—";
  const pct = (d * 100).toFixed(2);
  return d > 0 ? `+${pct}pp` : `${pct}pp`;
}

function americanToImplied(ml: number): number {
  if (ml >= 0) return 100 / (ml + 100);
  return -ml / (-ml + 100);
}

/** Expected value in dollars per $1 staked on the home side (same unit as backend `ev`). */
function fmtEV(ev: number | null | undefined): string {
  if (ev == null || Number.isNaN(ev)) return "—";
  const abs = Math.abs(ev).toFixed(2);
  return ev >= 0 ? `+$${abs}` : `-$${abs}`;
}

function fmtKelly(k: number | null | undefined): string {
  if (k == null || Number.isNaN(k)) return "";
  return `${(k * 100).toFixed(2)}% bk`;
}

type EdgeInfo = { label: string; bg: string; fg: string } | null;

function edgeInfo(edge: string | null | undefined): EdgeInfo {
  if (edge === "strong") {
    return { label: "strong", bg: "rgba(74, 222, 128, 0.15)", fg: "#4ade80" };
  }
  if (edge === "lean") {
    return { label: "lean", bg: "rgba(14, 165, 233, 0.12)", fg: "#38bdf8" };
  }
  return null;
}

function fmtSpread(s: number | null | undefined): string {
  if (s == null || Number.isNaN(s)) return "—";
  const x = Number(s.toFixed(2));
  if (x === 0) return "PK";
  const str = x.toFixed(2);
  return x > 0 ? `+${str}` : str;
}

function fmtTotal(t: number | null | undefined): string {
  if (t == null || Number.isNaN(t)) return "—";
  return Number(t).toFixed(2);
}

function fmtGameDate(raw: string): string {
  if (!raw) return "—";
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return raw.slice(0, 10);
  return d.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

function fmtIsoDate(raw: string | null | undefined): string {
  if (!raw) return "—";
  const d = new Date(`${raw}T00:00:00`);
  if (Number.isNaN(d.getTime())) return raw;
  return d.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

type SortKey = "date" | "baseline" | "user" | "delta" | "ev" | "gap";

/** Calendar YYYY-MM-DD in the browser's local timezone (date input values). */
function localTodayYmd(): string {
  const d = new Date();
  const y = d.getFullYear();
  const mo = String(d.getMonth() + 1).padStart(2, "0");
  const da = String(d.getDate()).padStart(2, "0");
  return `${y}-${mo}-${da}`;
}

/** Best-effort YYYY-MM-DD from API game_date without timezone skew. */
function gameDateYmd(raw: string): string {
  if (!raw) return "";
  const m = raw.match(/^(\d{4}-\d{2}-\d{2})/);
  if (m) return m[1];
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return "";
  const y = d.getFullYear();
  const mo = String(d.getMonth() + 1).padStart(2, "0");
  const da = String(d.getDate()).padStart(2, "0");
  return `${y}-${mo}-${da}`;
}

function clampYmd(ymd: string, min: string, max: string): string {
  if (!ymd) return min;
  if (ymd < min) return min;
  if (ymd > max) return max;
  return ymd;
}

function catalogDateBounds(
  games: SlateGame[] | undefined,
): { min: string; max: string } | null {
  if (!games?.length) return null;
  const ymds = games.map((g) => gameDateYmd(g.game_date)).filter(Boolean);
  if (!ymds.length) return null;
  return {
    min: ymds.reduce((a, b) => (a < b ? a : b)),
    max: ymds.reduce((a, b) => (a > b ? a : b)),
  };
}

function dateRangeMatch(g: SlateGame, start: string, end: string): boolean {
  const y = gameDateYmd(g.game_date);
  if (!y) return false;
  if (start && y < start) return false;
  if (end && y > end) return false;
  return true;
}

function gameDateMs(raw: string): number {
  const d = new Date(raw);
  return Number.isNaN(d.getTime()) ? 0 : d.getTime();
}

function numOrNull(v: number | null | undefined): number | null {
  if (v == null || Number.isNaN(v)) return null;
  return v;
}

function cmpNum(a: number | null, b: number | null, mul: number): number {
  if (a == null && b == null) return 0;
  if (a == null) return 1;
  if (b == null) return -1;
  return mul * (a - b);
}

function teamFilterMatch(g: SlateGame, q: string): boolean {
  const s = q.trim().toLowerCase();
  if (!s) return true;
  return (
    g.home_team.toLowerCase().includes(s) ||
    g.away_team.toLowerCase().includes(s)
  );
}

function sortGames(
  games: SlateGame[],
  sortKey: SortKey,
  sortDir: "asc" | "desc",
): SlateGame[] {
  const mul = sortDir === "asc" ? 1 : -1;
  return [...games].sort((a, b) => {
    switch (sortKey) {
      case "date":
        // Prefer YYYY-MM-DD lexical sort to avoid timezone skew.
        return (
          mul *
          (gameDateYmd(a.game_date).localeCompare(gameDateYmd(b.game_date)) ||
            (gameDateMs(a.game_date) - gameDateMs(b.game_date)))
        );
      case "baseline":
        return cmpNum(
          numOrNull(a.p_home_baseline),
          numOrNull(b.p_home_baseline),
          mul,
        );
      case "user":
        return cmpNum(numOrNull(a.p_home_user), numOrNull(b.p_home_user), mul);
      case "delta":
        return cmpNum(
          numOrNull(a.delta_user_vs_baseline),
          numOrNull(b.delta_user_vs_baseline),
          mul,
        );
      case "ev":
        return cmpNum(numOrNull(a.ev), numOrNull(b.ev), mul);
      case "gap": {
        const gapA =
          a.p_home_baseline != null && a.offered_ml != null
            ? a.p_home_baseline - americanToImplied(a.offered_ml)
            : null;
        const gapB =
          b.p_home_baseline != null && b.offered_ml != null
            ? b.p_home_baseline - americanToImplied(b.offered_ml)
            : null;
        return cmpNum(numOrNull(gapA), numOrNull(gapB), mul);
      }
      default:
        return 0;
    }
  });
}

function describeWindow(w: SlateWindow | undefined, allMode: boolean): string {
  if (allMode) return "Showing every prediction row (past and future), ignoring the date window.";
  if (!w) return "";
  if (w.from == null && w.to == null) return "Showing all games with predictions.";
  const upcoming =
    w.upcoming_only !== false &&
    w.upcoming_only !== null &&
    w.upcoming_only !== undefined;
  const scope = upcoming
    ? "Upcoming games that are not final yet"
    : "Games in this window (including completed)";
  if (w.from && w.to && w.from === w.to) {
    return `${scope}, scheduled on ${fmtIsoDate(w.from)}.`;
  }
  return `${scope}, scheduled ${fmtIsoDate(w.from)} — ${fmtIsoDate(w.to)}.`;
}

type ConfidenceBadge = { label: string; title: string } | null;

function confidenceBadge(g: SlateGame): ConfidenceBadge {
  if (g.low_confidence !== true) return null;
  const cov = g.coverage == null ? null : Math.round(g.coverage * 100);
  const coverageText =
    cov == null ? "coverage unknown" : `${cov}% feature coverage`;
  const nanText =
    g.n_nan_features == null
      ? ""
      : ` · ${g.n_nan_features} missing feature${g.n_nan_features === 1 ? "" : "s"}`;
  return {
    label: "low confidence",
    title: `Prediction uses limited data (${coverageText}${nanText}). Common for early-season or just-scheduled games where rolling-window stats aren't available yet.`,
  };
}

type ProbStyle = "bar" | "number" | "gauge";

function InfoTooltip({ text }: { text: string }) {
  const [open, setOpen] = useState(false);
  return (
    <span
      className="info-tip"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      <button
        type="button"
        className="info-tip-trigger"
        aria-label="More information"
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
      >
        ?
      </button>
      {open && (
        <span role="tooltip" className="info-tip-pop">
          {text}
        </span>
      )}
    </span>
  );
}

function WinBar({
  pHome,
  probStyle,
}: {
  pHome: number | null | undefined;
  probStyle: ProbStyle;
}) {
  if (pHome == null || Number.isNaN(pHome)) {
    return <span className="cell-dash">—</span>;
  }
  const homeWin = pHome * 100;
  const awayWin = 100 - homeWin;
  const isHomeFav = homeWin > 50;

  if (probStyle === "number") {
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <span
          style={{
            fontSize: 14,
            fontWeight: 600,
            color: isHomeFav ? "#e8e9f0" : "#8a8ca8",
          }}
        >
          {homeWin.toFixed(1)}%
        </span>
        <span className="win-bar-away-label">Away {awayWin.toFixed(1)}%</span>
      </div>
    );
  }

  if (probStyle === "gauge") {
    const angle = (homeWin / 100) * 180 - 90;
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 3,
        }}
      >
        <svg width="48" height="26" viewBox="0 0 48 26" aria-hidden>
          <path
            d="M4 24 A20 20 0 0 1 44 24"
            fill="none"
            stroke="#2e3050"
            strokeWidth="4"
            strokeLinecap="round"
          />
          <path
            d="M4 24 A20 20 0 0 1 44 24"
            fill="none"
            stroke={isHomeFav ? "#4ade80" : "#f87171"}
            strokeWidth="4"
            strokeLinecap="round"
            strokeDasharray={`${(homeWin / 100) * 62.8} 62.8`}
          />
          <line
            x1="24"
            y1="24"
            x2={24 + 18 * Math.cos(((angle - 90) * Math.PI) / 180)}
            y2={24 + 18 * Math.sin(((angle - 90) * Math.PI) / 180)}
            stroke="#e8e9f0"
            strokeWidth="1.5"
            strokeLinecap="round"
          />
        </svg>
        <span style={{ fontSize: 12, fontWeight: 600 }}>{homeWin.toFixed(1)}%</span>
      </div>
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
      <div className="win-bar-row">
        <div className="win-bar-track">
          <div
            className={`win-bar-fill ${isHomeFav ? "win-bar-fill--home" : "win-bar-fill--away"}`}
            style={{ width: `${homeWin}%` }}
          />
        </div>
        <span
          className={`win-bar-pct ${isHomeFav ? "win-bar-pct--strong" : "win-bar-pct--muted"}`}
        >
          {homeWin.toFixed(1)}%
        </span>
      </div>
      <span className="win-bar-away-label">Away {awayWin.toFixed(1)}%</span>
    </div>
  );
}

function MlCell({ ml }: { ml: number | null | undefined }) {
  if (ml == null || Number.isNaN(ml)) {
    return <span className="cell-dash">—</span>;
  }
  const n = Math.round(ml);
  const pos = n > 0;
  return (
    <span className={`ml-badge ${pos ? "ml-badge--pos" : "ml-badge--neg"}`}>
      {pos ? "+" : ""}
      {n}
    </span>
  );
}

function SlateUpcomingRow({
  g,
  showDate,
  probStyle,
  showMarketColumns,
}: {
  g: SlateGame;
  showDate: boolean;
  probStyle: ProbStyle;
  showMarketColumns: boolean;
}) {
  const badge = confidenceBadge(g);
  const isLow = g.low_confidence === true;
  const gapVal =
    g.p_home_baseline != null && g.offered_ml != null
      ? g.p_home_baseline - americanToImplied(g.offered_ml)
      : null;

  return (
    <tr className={isLow ? "slate-row-low" : undefined}>
      {showDate && (
        <td className="muted small">{fmtGameDate(g.game_date)}</td>
      )}
      <td>
        <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
          <span className="matchup-away">{g.away_team}</span>
          <span className="matchup-home">@ {g.home_team}</span>
          {badge && (
            <span className="badge-low-confidence" title={badge.title}>
              {badge.label}
            </span>
          )}
        </div>
      </td>
      <td className="slate-td-border">
        <WinBar pHome={g.p_home_baseline} probStyle={probStyle} />
      </td>
      <td>
        {g.p_home_user != null && !Number.isNaN(g.p_home_user) ? (
          <WinBar pHome={g.p_home_user} probStyle={probStyle} />
        ) : (
          <span className="cell-dash">—</span>
        )}
      </td>
      <td style={{ fontWeight: 600, fontVariantNumeric: "tabular-nums" }}>
        {g.delta_user_vs_baseline == null ? (
          <span className="cell-dash">—</span>
        ) : (
          <span
            className={
              g.delta_user_vs_baseline > 0 ? "delta-pos" : "delta-neg"
            }
          >
            {fmtDelta(g.delta_user_vs_baseline)}
          </span>
        )}
      </td>
      {showMarketColumns && (
        <>
          <td className="slate-td-border">
            <MlCell ml={g.offered_ml} />
          </td>
          <td>
            {g.offered_ml != null ? (
              <span className="muted small">
                {fmtProb(americanToImplied(g.offered_ml))}
              </span>
            ) : (
              <span className="cell-dash">—</span>
            )}
          </td>
          <td
            className={
              gapVal == null ? "" : gapVal > 0 ? "delta-pos" : "delta-neg"
            }
            style={{ fontWeight: 500 }}
          >
            {gapVal == null ? (
              <span className="cell-dash">—</span>
            ) : (
              `${gapVal > 0 ? "+" : ""}${(gapVal * 100).toFixed(1)}`
            )}
          </td>
        </>
      )}
      <td className="slate-td-border">
        <MlCell
          ml={(() => {
            const p = g.p_home_baseline;
            if (p == null || Number.isNaN(p) || p <= 0 || p >= 1) return null;
            const raw =
              p >= 0.5 ? -(p / (1 - p)) * 100 : ((1 - p) / p) * 100;
            return Math.round(raw);
          })()}
        />
      </td>
      <td
        className={
          g.ev == null ? "" : g.ev >= 0 ? "delta-pos" : "delta-neg"
        }
        style={{ fontWeight: g.ev != null && g.ev >= 0 ? 600 : undefined }}
      >
        {g.ev == null ? <span className="cell-dash">—</span> : fmtEV(g.ev)}
      </td>
      <td>
        {(() => {
          const info = edgeInfo(g.edge);
          return (
            <>
              {info ? (
                <span
                  style={{
                    display: "inline-block",
                    padding: "0.1rem 0.4rem",
                    borderRadius: "0.3rem",
                    fontSize: "0.78rem",
                    background: info.bg,
                    color: info.fg,
                    fontWeight: 600,
                  }}
                >
                  {info.label}
                </span>
              ) : (
                <span className="cell-dash" style={{ fontSize: "0.78rem" }}>
                  —
                </span>
              )}
              {g.bet && g.kelly_capped != null && (
                <span
                  className="muted small"
                  style={{ display: "block", marginTop: "0.1rem" }}
                >
                  {fmtKelly(g.kelly_capped)}
                </span>
              )}
            </>
          );
        })()}
      </td>
      <td>
        {g.opening_spread_home != null && (
          <span className="muted small">{fmtSpread(g.opening_spread_home)}</span>
        )}
        {g.model_spread_home != null && (
          <span
            className="muted small"
            style={{ display: "block", marginTop: "0.15rem" }}
            title="Margin model predicted spread (home perspective). Negative = model sees home as favorite."
          >
            Model: {fmtSpread(g.model_spread_home)}
          </span>
        )}
        {g.ats_gap != null && (
          <span
            className={`muted small ${g.ats_gap > 0 ? "delta-pos" : "delta-neg"}`}
            style={{
              display: "block",
              fontWeight: 600,
              marginTop: "0.05rem",
            }}
            title="ATS gap: how many points the model expects home to exceed the market spread. Positive = model expects home to cover."
          >
            {g.ats_gap > 0 ? "+" : ""}
            {g.ats_gap.toFixed(2)} ATS
          </span>
        )}
        {(g.opening_total != null || g.mu_total != null) && (
          <div
            className="muted small"
            style={{ marginTop: "0.35rem", fontSize: "0.6875rem" }}
          >
            O/U{" "}
            {g.opening_total != null
              ? fmtTotal(g.opening_total)
              : g.mu_total != null
                ? fmtTotal(g.mu_total)
                : "—"}
            {g.mu_total != null && g.opening_total != null && (
              <span
                className="muted small"
                style={{ display: "block", marginTop: "0.15rem" }}
                title="Model total (home pts + away pts predicted by totals model)"
              >
                Model: {fmtTotal(g.mu_total)}
              </span>
            )}
            {g.ou_edge != null && (
              <span
                className={g.ou_edge > 0 ? "delta-pos" : "delta-neg"}
                style={{
                  display: "block",
                  fontWeight: 600,
                  marginTop: "0.05rem",
                }}
                title="O/U gap: model total minus market total. Positive = model leans Over; negative = model leans Under."
              >
                {g.ou_edge > 0 ? "▲" : "▼"} {g.ou_edge > 0 ? "+" : ""}
                {g.ou_edge.toFixed(2)} O/U
              </span>
            )}
          </div>
        )}
        {g.opening_spread_home == null &&
          g.model_spread_home == null &&
          g.opening_total == null &&
          g.mu_total == null && <span className="cell-dash">—</span>}
      </td>
    </tr>
  );
}

export function SlatePage() {
  const { user } = useAuth();
  const { slate: slatePrefs, updateSlate } = useDisplayPreferences();
  const [slate, setSlate] = useState<SlateResponse | null>(null);
  const [history, setHistory] = useState<PredictionHistoryResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [historyError, setHistoryError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [allMode, setAllMode] = useState(false);
  const [teamFilter, setTeamFilter] = useState("");
  const [sortKey, setSortKey] = useState<SortKey>("date");
  /** Date column: ascending = next upcoming first; other columns default to descending. */
  const [sortDir, setSortDir] = useState<"asc" | "desc">("asc");
  const [rangeStart, setRangeStart] = useState(() => localTodayYmd());
  const [rangeEnd, setRangeEnd] = useState("");
  const [displayOptsOpen, setDisplayOptsOpen] = useState(false);
  const [infoOpen, setInfoOpen] = useState(false);
  const [showAllColumns, setShowAllColumns] = useState(false);

  useEffect(() => {
    if (!user) return;
    const ctrl = new AbortController();
    setLoading(true);
    setError(null);
    setHistoryError(null);
    void Promise.allSettled([
      api.getSlate({
        idToken: user.idToken,
        signal: ctrl.signal,
        all: allMode || undefined,
      }),
      api.getPredictionHistory({
        idToken: user.idToken,
        signal: ctrl.signal,
      }),
    ])
      .then((results) => {
        const [slateRes, histRes] = results;
        if (slateRes.status === "fulfilled") setSlate(slateRes.value);
        else if (slateRes.reason?.name !== "AbortError")
          setError(String(slateRes.reason?.message ?? slateRes.reason));
        if (histRes.status === "fulfilled") setHistory(histRes.value);
        else if (histRes.reason?.name !== "AbortError")
          setHistoryError(String(histRes.reason?.message ?? histRes.reason));
      })
      .finally(() => setLoading(false));
    return () => ctrl.abort();
  }, [user, allMode]);

  const windowText = useMemo(
    () => describeWindow(slate?.window, allMode),
    [slate?.window, allMode],
  );

  const bounds = useMemo(
    () => catalogDateBounds(slate?.games),
    [slate?.games],
  );

  const dateRangeDefaults = useMemo(() => {
    if (!bounds) return null;
    return {
      start: clampYmd(localTodayYmd(), bounds.min, bounds.max),
      end: bounds.max,
    };
  }, [bounds]);

  useEffect(() => {
    if (!dateRangeDefaults) return;
    setRangeEnd(dateRangeDefaults.end);
    setRangeStart(dateRangeDefaults.start);
  }, [slate?.generated_at, allMode, dateRangeDefaults]);

  const endForFilter = rangeEnd || bounds?.max || "";
  const startForFilter = rangeStart;

  const displayedGames = useMemo(() => {
    if (!slate?.games?.length) return [];
    const filtered = slate.games
      .filter((g) => teamFilterMatch(g, teamFilter))
      .filter((g) => dateRangeMatch(g, startForFilter, endForFilter));
    return sortGames(filtered, sortKey, sortDir);
  }, [
    slate?.games,
    teamFilter,
    sortKey,
    sortDir,
    startForFilter,
    endForFilter,
  ]);

  const hasMarket = useMemo(
    () => displayedGames.some((g) => g.offered_ml != null),
    [displayedGames],
  );

  const showMarketColumns =
    showAllColumns || !slatePrefs.hide_empty_market || hasMarket;

  const dateGroups = useMemo(() => {
    if (!slatePrefs.group_by_date) return null;
    const order: string[] = [];
    const seen = new Set<string>();
    for (const g of displayedGames) {
      const label = fmtGameDate(g.game_date);
      if (!seen.has(label)) {
        seen.add(label);
        order.push(label);
      }
    }
    return order.map((date) => ({
      date,
      games: displayedGames.filter((g) => fmtGameDate(g.game_date) === date),
    }));
  }, [displayedGames, slatePrefs.group_by_date]);

  const baseColCount = slatePrefs.group_by_date ? 1 : 2;
  const totalTableCols =
    baseColCount + 3 + (showMarketColumns ? 3 : 0) + 4;

  const windowChipText = useMemo(() => {
    if (allMode) return "All predictions · past and future";
    const w = slate?.window;
    if (!w) return "";
    if (w.from && w.to) {
      const upcoming =
        w.upcoming_only !== false &&
        w.upcoming_only !== null &&
        w.upcoming_only !== undefined;
      const prefix = upcoming ? "Upcoming" : "Window";
      return `${prefix} · ${fmtIsoDate(w.from)} — ${fmtIsoDate(w.to)}`;
    }
    return "Predictions";
  }, [slate?.window, allMode]);

  const onSortClick = (key: SortKey) => {
    if (key === sortKey) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir(key === "date" ? "asc" : "desc");
    }
  };

  const sortIndicator = (key: SortKey) =>
    sortKey === key ? (sortDir === "asc" ? "↑" : "↓") : "";

  if (!user) {
    return (
      <section className="page">
        <h1>Predictions</h1>
        <p className="muted">
          Sign in with Google to see upcoming games and recent results: baseline
          probabilities and, if you&apos;ve trained one, your custom model
          alongside them.
        </p>
      </section>
    );
  }

  return (
    <section className="page">
      <div className="slate-page-head">
        <div className="slate-page-head-main">
          <h1>Predictions</h1>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "0.625rem",
              flexWrap: "wrap",
            }}
          >
            {windowChipText && (
              <span className="slate-window-chip">{windowChipText}</span>
            )}
            {slate && (
              <span className="slate-generated-meta">
                Generated {new Date(slate.generated_at).toLocaleString()}
                {slate.has_user_model
                  ? " · baseline vs. your model"
                  : " · baseline only"}
              </span>
            )}
          </div>
        </div>
        <button
          type="button"
          className="slate-info-btn"
          aria-expanded={infoOpen}
          onClick={() => setInfoOpen((o) => !o)}
        >
          <span>How this works</span>
          <span className="slate-info-chevron" aria-hidden>
            ▾
          </span>
        </button>
      </div>

      {infoOpen && (
        <div className="slate-info-panel">
          <div className="slate-info-grid">
            <div>
              <strong>Scope</strong> — {windowText}
            </div>
            <div>
              <strong>Upcoming</strong> — predicted probability the home team
              wins, for not-yet-final games in the server&apos;s rolling window
              (UTC).
            </div>
            <div>
              <strong>Baseline</strong> — league model. If you&apos;ve trained a
              Model Lab run, <em>Your model</em> and Δ (percentage points) appear.
            </div>
            <div>
              <strong>Market columns</strong> — populate when opening lines are
              available from the pipeline. Schedule-only rows usually show —.
            </div>
            <div>
              <strong>Model spread / O/U</strong> — appears when the model bundle
              includes margin and totals outputs joined at score time.
            </div>
            <div>
              <strong>Recent results</strong> — completed games from the current
              calendar year only; see the section below the slate table.
            </div>
          </div>
        </div>
      )}

      {loading && <p className="muted">Loading…</p>}
      {error && <p className="error">{error}</p>}

      {slate && (slate.games?.length ?? 0) === 0 && (
        <p className="muted">No games scheduled in this window.</p>
      )}

      {slate && (slate.games?.length ?? 0) > 0 && (
        <>
          <div className="slate-filters">
            <label className="field slate-team-filter">
              <span>Team</span>
              <div className="slate-search-wrap">
                <span className="slate-search-icon" aria-hidden>
                  ⌕
                </span>
                <input
                  type="search"
                  placeholder="Filter by team…"
                  value={teamFilter}
                  onChange={(e) => setTeamFilter(e.target.value)}
                  autoComplete="off"
                  spellCheck={false}
                />
              </div>
            </label>
            {bounds && (
              <div className="slate-date-range">
                <label className="field">
                  <span>From</span>
                  <input
                    type="date"
                    value={rangeStart}
                    min={bounds.min}
                    max={bounds.max}
                    onChange={(e) => setRangeStart(e.target.value)}
                  />
                </label>
                <label className="field">
                  <span>To</span>
                  <input
                    type="date"
                    value={rangeEnd || bounds.max}
                    min={bounds.min}
                    max={bounds.max}
                    onChange={(e) => setRangeEnd(e.target.value)}
                  />
                </label>
                <button
                  type="button"
                  className="slate-filter-reset"
                  onClick={() => {
                    if (!dateRangeDefaults) return;
                    setRangeEnd(dateRangeDefaults.end);
                    setRangeStart(dateRangeDefaults.start);
                    setTeamFilter("");
                  }}
                  disabled={
                    !dateRangeDefaults ||
                    (rangeStart === dateRangeDefaults.start &&
                      (rangeEnd || bounds.max) === dateRangeDefaults.end &&
                      teamFilter.trim() === "")
                  }
                >
                  Reset
                </button>
              </div>
            )}
            <div className="slate-filters-extras">
              <button
                type="button"
                className="slate-display-inline-btn"
                onClick={() => setDisplayOptsOpen(true)}
              >
                Display options…
              </button>
              <label className="slate-checkbox">
                <input
                  type="checkbox"
                  checked={showAllColumns}
                  onChange={(e) => setShowAllColumns(e.target.checked)}
                />
                Show all columns
              </label>
              <label className="slate-checkbox">
                <input
                  type="checkbox"
                  checked={allMode}
                  onChange={(e) => setAllMode(e.target.checked)}
                />
                Show all predictions
              </label>
            </div>
          </div>

          {(teamFilter.trim() !== "" ||
            displayedGames.length !== slate.games.length) && (
            <p className="muted small" style={{ marginTop: "-0.35rem" }}>
              {displayedGames.length} of {slate.games.length} games
            </p>
          )}

          {displayedGames.length === 0 ? (
            <p className="muted">No games match these filters.</p>
          ) : (
            <div className="slate-table-shell">
              <div className="slate-table-scroll">
                <table
                  className={`slate-table slate-table--density-${slatePrefs.table_density}`}
                >
                  <thead>
                    <tr>
                      <td
                        colSpan={baseColCount}
                        className="slate-colgroup-label slate-colgroup-label--spacer"
                      >
                        {" "}
                      </td>
                      <td
                        colSpan={3}
                        className="slate-colgroup-label slate-colgroup-label--model"
                      >
                        Model prediction
                      </td>
                      {showMarketColumns && (
                        <td
                          colSpan={3}
                          className="slate-colgroup-label slate-colgroup-label--market"
                        >
                          Market
                        </td>
                      )}
                      <td
                        colSpan={4}
                        className="slate-colgroup-label slate-colgroup-label--bet"
                      >
                        Betting
                      </td>
                    </tr>
                    <tr>
                      {!slatePrefs.group_by_date && (
                        <th
                          scope="col"
                          className="slate-th"
                          style={{ width: 110 }}
                          {...(sortKey === "date"
                            ? {
                                "aria-sort":
                                  sortDir === "asc" ? "ascending" : "descending",
                              }
                            : {})}
                        >
                          <button
                            type="button"
                            className="th-sort"
                            onClick={() => onSortClick("date")}
                            title="Sort by game date (ascending: next upcoming first)"
                          >
                            Date
                            <span className="th-sort-dir" aria-hidden>
                              {sortIndicator("date")}
                            </span>
                          </button>
                        </th>
                      )}
                      <th scope="col" className="slate-th">
                        Matchup
                      </th>
                      <th
                        scope="col"
                        className="slate-th slate-th--border"
                        {...(sortKey === "baseline"
                          ? {
                              "aria-sort":
                                sortDir === "asc" ? "ascending" : "descending",
                            }
                          : {})}
                      >
                        <span className="th-tip-wrap">
                          <button
                            type="button"
                            className="th-sort"
                            onClick={() => onSortClick("baseline")}
                            title="Sort by baseline home win probability"
                          >
                            Home win %
                            <span className="th-sort-dir" aria-hidden>
                              {sortIndicator("baseline")}
                            </span>
                          </button>
                          <InfoTooltip text="Predicted probability the home team wins — baseline league model." />
                        </span>
                      </th>
                      <th
                        scope="col"
                        className="slate-th"
                        {...(sortKey === "user"
                          ? {
                              "aria-sort":
                                sortDir === "asc" ? "ascending" : "descending",
                            }
                          : {})}
                      >
                        <span className="th-tip-wrap">
                          <button
                            type="button"
                            className="th-sort"
                            onClick={() => onSortClick("user")}
                            title="Sort by your model home win probability"
                          >
                            Your model
                            <span className="th-sort-dir" aria-hidden>
                              {sortIndicator("user")}
                            </span>
                          </button>
                          <InfoTooltip text="Win% from your trained Model Lab run. Requires a completed run." />
                        </span>
                      </th>
                      <th
                        scope="col"
                        className="slate-th"
                        {...(sortKey === "delta"
                          ? {
                              "aria-sort":
                                sortDir === "asc" ? "ascending" : "descending",
                            }
                          : {})}
                      >
                        <span className="th-tip-wrap">
                          <button
                            type="button"
                            className="th-sort"
                            onClick={() => onSortClick("delta")}
                            title="Sort by delta (your model minus baseline)"
                          >
                            Δ (pp)
                            <span className="th-sort-dir" aria-hidden>
                              {sortIndicator("delta")}
                            </span>
                          </button>
                          <InfoTooltip text="Difference in percentage points between your model and baseline." />
                        </span>
                      </th>
                      {showMarketColumns && (
                        <>
                          <th
                            scope="col"
                            className="slate-th slate-th--border"
                          >
                            <span className="th-tip-wrap">
                              Market ML
                              <InfoTooltip text="Opening moneyline from the market. Requires pipeline data." />
                            </span>
                          </th>
                          <th scope="col" className="slate-th">
                            <span className="th-tip-wrap">
                              Implied%
                              <InfoTooltip text="Market-implied win probability from the opening moneyline." />
                            </span>
                          </th>
                          <th
                            scope="col"
                            className="slate-th"
                            {...(sortKey === "gap"
                              ? {
                                  "aria-sort":
                                    sortDir === "asc"
                                      ? "ascending"
                                      : "descending",
                                }
                              : {})}
                          >
                            <span className="th-tip-wrap">
                              <button
                                type="button"
                                className="th-sort"
                                onClick={() => onSortClick("gap")}
                                title="Sort by model vs market gap (model probability minus book-implied probability)."
                              >
                                Gap
                                <span className="th-sort-dir" aria-hidden>
                                  {sortIndicator("gap")}
                                </span>
                              </button>
                              <InfoTooltip text="Difference between model probability and market-implied probability." />
                            </span>
                          </th>
                        </>
                      )}
                      <th scope="col" className="slate-th slate-th--border">
                        <span className="th-tip-wrap">
                          Model ML
                          <InfoTooltip text="Model moneyline — converted from win probability." />
                        </span>
                      </th>
                      <th
                        scope="col"
                        className="slate-th"
                        {...(sortKey === "ev"
                          ? {
                              "aria-sort":
                                sortDir === "asc" ? "ascending" : "descending",
                            }
                          : {})}
                      >
                        <span className="th-tip-wrap">
                          <button
                            type="button"
                            className="th-sort"
                            onClick={() => onSortClick("ev")}
                            title="Sort by expected value per $1 staked on home team"
                          >
                            EV / $1
                            <span className="th-sort-dir" aria-hidden>
                              {sortIndicator("ev")}
                            </span>
                          </button>
                          <InfoTooltip text="Expected value in dollars per $1 staked on the home side." />
                        </span>
                      </th>
                      <th scope="col" className="slate-th">
                        <span className="th-tip-wrap">
                          Edge / Stake
                          <InfoTooltip text="Edge classification and suggested bankroll stake (quarter-Kelly, capped at 2%)." />
                        </span>
                      </th>
                      <th scope="col" className="slate-th">
                        <span className="th-tip-wrap">
                          Spread / O/U
                          <InfoTooltip text="Opening spread, model spread, ATS gap, totals line, model total, and O/U edge when available." />
                        </span>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {slatePrefs.group_by_date && dateGroups
                      ? dateGroups.map(({ date, games }) => (
                          <Fragment key={date}>
                            <tr className="slate-date-group-row">
                              <td colSpan={totalTableCols}>{date}</td>
                            </tr>
                            {games.map((g) => (
                              <SlateUpcomingRow
                                key={g.game_id}
                                g={g}
                                showDate={false}
                                probStyle={slatePrefs.prob_style}
                                showMarketColumns={showMarketColumns}
                              />
                            ))}
                          </Fragment>
                        ))
                      : displayedGames.map((g) => (
                          <SlateUpcomingRow
                            key={g.game_id}
                            g={g}
                            showDate
                            probStyle={slatePrefs.prob_style}
                            showMarketColumns={showMarketColumns}
                          />
                        ))}
                  </tbody>
                </table>
              </div>
              <div className="slate-table-footer">
                <span className="slate-table-footer-meta">
                  {displayedGames.length} games · Market data available for{" "}
                  {displayedGames.filter((x) => x.offered_ml != null).length}
                </span>
                <div className="slate-legend">
                  <span className="slate-legend-item">
                    <span
                      className="slate-legend-swatch slate-legend-swatch--home"
                      aria-hidden
                    />
                    Home favored
                  </span>
                  <span className="slate-legend-item">
                    <span
                      className="slate-legend-swatch slate-legend-swatch--away"
                      aria-hidden
                    />
                    Away favored
                  </span>
                  <span className="slate-legend-item slate-legend-low-text">
                    <span
                      className="slate-legend-swatch slate-legend-swatch--low"
                      aria-hidden
                    />
                    Low confidence
                  </span>
                </div>
              </div>
            </div>
          )}
        </>
      )}

      <h2 className="slate-section-title slate-section-title--follow">
        Recent results
      </h2>
      <p className="muted">
        Baseline pick (home win if probability ≥ 50%) vs actual outcome. Scoped
        to the current UTC calendar year; older seasons are omitted.
      </p>
      {historyError && <p className="error">{historyError}</p>}
      {history && (
        <p className="muted">
          {history.summary.n_games === 0 ? (
            <>No completed games in the predictions file for this year yet.</>
          ) : (
            <>
              This year: {history.summary.n_correct} / {history.summary.n_games}{" "}
              correct (
              {history.summary.accuracy == null
                ? "—"
                : `${(history.summary.accuracy * 100).toFixed(1)}%`}
              ). Showing up to {history.filter?.row_limit ?? "—"} most recent.
            </>
          )}
        </p>
      )}
      {history && (history.games?.length ?? 0) > 0 && (
        <div className="slate-table-shell">
          <div className="slate-table-scroll">
            <table className="slate-table">
              <thead>
                <tr>
                  <th className="slate-th">Date</th>
                  <th className="slate-th">Matchup</th>
                  <th className="slate-th">P(home) baseline</th>
                  <th className="slate-th">Home won</th>
                  <th className="slate-th">Predicted home</th>
                  <th className="slate-th">Correct</th>
                </tr>
              </thead>
              <tbody>
                {history.games.map((g: PredictionHistoryGame) => (
                  <tr key={g.league_game_id}>
                    <td>{fmtGameDate(g.game_date)}</td>
                    <td>
                      <span className="matchup-away">{g.away_team_id}</span>{" "}
                      <span className="matchup-home">
                        @ {g.home_team_id}
                      </span>
                    </td>
                    <td>{fmtProb(g.p_home_base)}</td>
                    <td>
                      {g.home_win_actual == null
                        ? "—"
                        : g.home_win_actual
                          ? "Yes"
                          : "No"}
                    </td>
                    <td>
                      {g.predicted_home_win == null
                        ? "—"
                        : g.predicted_home_win
                          ? "Yes"
                          : "No"}
                    </td>
                    <td>
                      {g.correct == null ? "—" : g.correct ? "Yes" : "No"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {displayOptsOpen && (
        <button
          type="button"
          className="slate-display-backdrop"
          aria-label="Close display options"
          onClick={() => setDisplayOptsOpen(false)}
        />
      )}
      <button
        type="button"
        className={`slate-display-tab${displayOptsOpen ? " slate-display-tab--hidden" : ""}`}
        onClick={() => setDisplayOptsOpen(true)}
        title="Display options"
      >
        <span className="slate-display-tab-icon" aria-hidden>
          ⚙
        </span>
        <span className="slate-display-tab-label">Display</span>
      </button>
      <aside
        className={`slate-display-panel${displayOptsOpen ? " is-open" : ""}`}
        aria-hidden={!displayOptsOpen}
      >
        <div className="slate-display-panel-head">
          <h2 className="slate-display-panel-title">Display options</h2>
          <button
            type="button"
            className="slate-display-panel-close"
            onClick={() => setDisplayOptsOpen(false)}
            aria-label="Close display options"
          >
            ×
          </button>
        </div>
        <div className="slate-display-panel-body">
          <div className="slate-display-row">
            <span className="slate-display-label">Win% display</span>
            <div className="slate-segment" role="group" aria-label="Win percentage display style">
              {(["bar", "number", "gauge"] as const).map((s) => (
                <button
                  key={s}
                  type="button"
                  className={slatePrefs.prob_style === s ? "is-active" : undefined}
                  onClick={() => updateSlate({ prob_style: s })}
                >
                  {s === "bar" ? "Bar + %" : s === "number" ? "Number only" : "Gauge"}
                </button>
              ))}
            </div>
          </div>
          <div className="slate-display-row">
            <span className="slate-display-label">Table density</span>
            <div className="slate-segment" role="group" aria-label="Table density">
              {(["compact", "default", "relaxed"] as const).map((d) => (
                <button
                  key={d}
                  type="button"
                  className={slatePrefs.table_density === d ? "is-active" : undefined}
                  onClick={() => updateSlate({ table_density: d })}
                >
                  {d === "compact" ? "Compact" : d === "default" ? "Default" : "Relaxed"}
                </button>
              ))}
            </div>
          </div>
          <label className="slate-display-toggle">
            <input
              type="checkbox"
              checked={slatePrefs.hide_empty_market}
              onChange={(e) => updateSlate({ hide_empty_market: e.target.checked })}
            />
            <span>Hide empty market columns</span>
          </label>
          <label className="slate-display-toggle">
            <input
              type="checkbox"
              checked={slatePrefs.group_by_date}
              onChange={(e) => updateSlate({ group_by_date: e.target.checked })}
            />
            <span>Group rows by date</span>
          </label>
          <p className="muted small slate-display-foot">
            Table and win% styles sync to your account when signed in; this device keeps a local
            cache if the network is unavailable.
          </p>
        </div>
      </aside>
    </section>
  );
}
